import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationSection,
  CommonQuestionKeys,
  KCCustomQuestionKeys,
  SMBGrantCustomQuestionKeys,
} from '@bybeam/platform-types';
import STANDARD_COMPONENTS from '../standards/index.js';
import { SpecialSectionKeys } from '../standards/types.js';
import { QUESTIONS } from './questions/grantQuestions.js';
import { QUESTIONS as SHARED_QUESTIONS } from './questions/sharedQuestions.js';

const SECTIONS: ApplicationSection[] = [
  {
    key: 'personalInfo',
    name: 'Owner Information',
    overview: {
      title: 'Owner Information',
      description:
        'The information below should be completed by the business owner and/or managing partner (per your state registration).',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'About the Business Owner',
        questions: [
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_OWNER_NAME],
          SHARED_QUESTIONS[CommonQuestionKeys.EMAIL],
          SHARED_QUESTIONS[CommonQuestionKeys.PHONE_NUMBER],
          SHARED_QUESTIONS[KCCustomQuestionKeys.COMPANY_LEGAL_NAME],
          SHARED_QUESTIONS[KCCustomQuestionKeys.DOING_BUSINESS_AS],
          SHARED_QUESTIONS[KCCustomQuestionKeys.TITLE],
          SHARED_QUESTIONS[CommonQuestionKeys.ETHNICITY],
          SHARED_QUESTIONS[KCCustomQuestionKeys.PRODUCTS_SERVICES],
          SHARED_QUESTIONS[KCCustomQuestionKeys.EMPLOYEES],
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_ADDRESS],
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_LICENSE],
          SHARED_QUESTIONS[KCCustomQuestionKeys.TAX_CLEARANCE_LETTER],
          SHARED_QUESTIONS[KCCustomQuestionKeys.YEARS_IN_BUSINESS],
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_BANK_ACCOUNT],
          SHARED_QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS],
          SHARED_QUESTIONS[CommonQuestionKeys.HOUSEHOLD_SIZE],
          SHARED_QUESTIONS[KCCustomQuestionKeys.DEPENDENTS],
          SHARED_QUESTIONS[CommonQuestionKeys.HOUSEHOLD_INCOME],
          SHARED_QUESTIONS[KCCustomQuestionKeys.NET_WORTH_EXCEEDS],
        ],
      },
    ],
  },
  {
    key: 'aboutTheBusiness',
    name: 'Business Pitch',
    overview: {
      description: '',
      title: 'Business Pitch',
    },
    questionGroups: [
      {
        key: 'aboutTheBusiness',
        name: 'Business Pitch',
        questions: [
          QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_DESCRIPTION],
          QUESTIONS[SMBGrantCustomQuestionKeys.SOCIAL_IMPACT],
          QUESTIONS[SMBGrantCustomQuestionKeys.TEAM_AND_EXPERTISE],
          QUESTIONS[SMBGrantCustomQuestionKeys.PRIOR_FUNDING],
          QUESTIONS[SMBGrantCustomQuestionKeys.MARKET_POTENTIAL],
        ],
      },
    ],
  },
  {
    key: 'financialInformation',
    name: 'Financial Information',
    overview: {
      title: 'Financial Information',
      description: '',
    },
    questionGroups: [
      {
        key: 'financialInformation',
        name: 'Financial Information',
        questions: [
          QUESTIONS[SMBGrantCustomQuestionKeys.HAS_FINANCIAL_STATEMENTS],
          QUESTIONS[SMBGrantCustomQuestionKeys.YEARLY_SALES],
          QUESTIONS[SMBGrantCustomQuestionKeys.CASH_FLOW_POSITIVE],
          QUESTIONS[SMBGrantCustomQuestionKeys.REVENUE_GOAL],
          QUESTIONS[SMBGrantCustomQuestionKeys.CHALLENGES_AND_MITIGATION],
          QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_PLAN],
          QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_PLAN_DOCUMENTS],
        ],
      },
    ],
  },
  {
    key: 'attendance',
    name: 'Attendance Requirements',
    overview: {
      title: 'Attendance Requirements',
      description: '',
    },
    questionGroups: [
      {
        key: 'attendance',
        name: 'Attendance Requirements',
        questions: [
          QUESTIONS[SMBGrantCustomQuestionKeys.CAN_COMMIT_TO_SESSIONS],
          QUESTIONS[SMBGrantCustomQuestionKeys.CAN_COMPLETE_MONTHLY_PROGRESS_REPORTS],
          QUESTIONS[SMBGrantCustomQuestionKeys.ANY_CHALLENGES],
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        questionGroups: [
          // Drop the first question
          null,
          // Drop the first question and replace it with this one
          {
            questions: [null, QUESTIONS.affirmation],
          },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'default',
      title: 'KC Micro-Business Grant Application',
      body: `
  In efforts to assist eligible micro-businesses (less than 10 employees) that experienced negative economic impact from the pandemic, the City of Kansas City, Missouri, has partnered with the Economic Development Corporation – Loan Corporation (EDCLC) to help micro-businesses grow, expand, and retain jobs in Kansas City, Missouri.
  
  Grants are available to Kansas City, Missouri-based businesses operating prior to January 1, 2023, with ten (10) employees or less. The EDCLC will provide grants of $10,000, $15,000, and $25,000. Qualified applicants that own an existing micro-business that is primed for expansion and moving to the next level in business growth can utilize funds towards:

  - Overhead costs (Rent, lease, mortgage, etc.)
  - Renovation costs (Sheetrock, flooring, painting, etc.)
  - Technology (Laptop, desktops, etc.)
  - Professional services (Legal, accounting, etc.)
  - Facade and minor roof repairs (Painting, sealing, etc.)
  - Inventory costs (Except office supplies)
  - Equipment costs
  - Marketing (Website, signage, etc.)

  `,
    },
    {
      key: 'ineligible-businesses',
      layout: 'default',
      title: 'Ineligible Micro-Businesses',
      body: `
  1. Liquor stores, payday loans, hookah, vape shops, marijuana related businesses, and rental properties (long and short term). 
  2. Bars and establishments that sell alcohol with less than 50% in food sales (sites with state and federal license for production and distribution may apply).
  3. New businesses operating less than one year.
  `,
    },
    {
      key: 'technical-support-requirement',
      layout: 'default',
      title: 'Technical Support Requirement for Grant Recipients',
      body: `
  Selected grant recipients will be required to participate in the technical assistance training. Utilizing the foundations of the Kauffman FastTrac program, grant recipients will receive technical assistance focused on collateral and business assessments, business plan development or reconstruction, feasibility, and financial structuring. Additionally, they will set SMART goals and a Tactical Improvement Plan with a focus on expense and repayment accountability plans. This work includes 10 hours of individual 1:1 support or group training, if applicable.

  Before you begin, please review the program eligibility criteria carefully to ensure your business qualifies:

  1. Must be a private, for-profit business.
  2. Must employ ten (10) employees or less.
  3. Must maintain an active 2024 KCMO business license (upload a copy).
  4. Must maintain a principal place of business within the boundaries of KCMO.
  5. Must have been in business for at least one (1) year, i.e. established before January 1, 2023.
  `,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        referralMessage: 'You have been referred to this program.',
        message: `Capital assistance for small businesses 

Your business may be eligible for one of various forms of business development assistance, including educational and/or financial. Apply now!`,
      },
      {
        status: 'InProgress',
        label: '',
        referralMessage: 'You have been referred to this program.',
        message:
          'You’ve made great progress so far! Keep working on your application to receive business development assistance.',
      },
      {
        status: 'AwaitingReview',
        label: 'Status: Awaiting Review',
        message:
          'Thank you for submitting your application! You will be notified if our team needs additional information and/or of any next steps. Check back here for updates.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'review',
          title: 'Application review',
          icon: 'description',
          description:
            'While we look at your application, we might send you an email to ask for more information or notify you of next steps',
        },
      ],
    },
  },
  sections: SECTIONS,
};

export default CONFIG;
